// RichTextEditor.jsx
import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

const ALLOWED = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'a',
  'br',
  'p',
  'div',
  'span',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'colgroup',
  'col',
];

export default function RichTextEditor({
  html,
  onChange,
  className,
  placeholder,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // only update if coming from outside to avoid caret jumps
    if (ref.current.innerHTML !== html) {
      ref.current.innerHTML = html || '';
    }
  }, [html]);

  const handleKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      e.preventDefault();

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const range = sel.getRangeAt(0);
      const br = document.createElement('br');

      // insert a <br> at the caret
      range.insertNode(br);

      // move caret to just after the <br>
      range.setStartAfter(br);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      return;
    }
  };

  const handleInput = () => {
    const next = ref.current?.innerHTML || '';
    onChange(next);
  };

  const handlePaste = (e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const htmlData = cd.getData('text/html');
    const textData = cd.getData('text/plain');

    // Prefer HTML if present, fall back to plain text
    const raw =
      htmlData ||
      (textData ? `<p>${textData.replace(/\n/g, '<br/>')}</p>` : '');
    if (!raw) return;

    e.preventDefault();

    const clean = DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ALLOWED,
      ALLOWED_ATTR: [
        'href',
        'title',
        'style',
        'class',
        'colspan',
        'rowspan',
        'scope',
      ],
      FORBID_ATTR: ['on*'],
      ADD_ATTR: [],
      KEEP_CONTENT: true,
    });

    // Insert at caret
    const sel = window.getSelection();
    if (sel && sel.getRangeAt && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const temp = document.createElement('div');
      temp.innerHTML = clean;
      const frag = document.createDocumentFragment();
      while (temp.firstChild) frag.appendChild(temp.firstChild);
      range.insertNode(frag);
      // move caret to end
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      handleInput(); // bubble change
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onKeyDown={handleKeyDown}
      onInput={handleInput}
      onPaste={handlePaste}
      style={{
        minHeight: 300,
        minWidth: '100%',
        marginTop: 15,
        paddingLeft: 20,
        border: '1px solid #E5E7EB',
        borderRadius: 8,
      }}
    />
  );
}
